import { z } from 'zod';

import { apiClient } from './client';

// Leases API — tenant views own active lease(s).

export const LeaseStatusZ = z.enum(['draft', 'active', 'expired', 'terminated']);
export const SigningStatusZ = z.enum([
  'draft',
  'pending_owner',
  'pending_tenant',
  'fully_signed',
  'expired',
]);
export const UtilityModeZ = z.enum(['tenant_pays', 'owner_bills', 'included']);

export const PropertyBriefZ = z.object({
  id: z.number().int(),
  address: z.string(),
  city: z.string(),
  district: z.string().nullable().optional(),
  area_sqm: z.number().optional(),
  rooms: z.number().int().nullable().optional(),
  // backend отдаёт photos массивом ОБЪЕКТОВ ({index,path,url,uploaded_at}), а
  // не строк -- объект с фото ронял парсинг всего списка договоров. На вебе эти
  // фото не рендерятся, поэтому достаточно толерантной схемы.
  photos: z.array(z.unknown()).default([]),
  type: z.string(),
});

export const LeaseZ = z.object({
  id: z.number().int(),
  property_id: z.number().int(),
  tenant_id: z.number().int(),
  start_date: z.string(),
  end_date: z.string(),
  monthly_rent: z.number(),
  deposit_amount: z.number(),
  payment_day: z.number().int(),
  utility_mode: UtilityModeZ,
  utility_fixed_amount: z.number().nullable().optional(),
  // terms -- свободный JSON-map: булевы флаги И не-булевы значения (напр.
  // termination_reason у расторгнутого договора). Пустой backend отдаёт как [].
  // Иначе один такой договор ронял бы парсинг всего списка.
  terms: z
    .preprocess((v) => (Array.isArray(v) ? {} : v), z.record(z.string(), z.unknown()))
    .default({}),
  status: LeaseStatusZ,
  // .catch(undefined): незнакомое значение не должно ронять парсинг всего списка
  // (как было с document.type) -- деградируем в undefined.
  signing_status: SigningStatusZ.optional().catch(undefined),
  contract_pdf_path: z.string().nullable().optional(),
  property: PropertyBriefZ.optional(),
});
export type Lease = z.infer<typeof LeaseZ>;

const ListLeasesZ = z.object({ data: z.array(LeaseZ) });
const ResourceLeaseZ = z.object({ data: LeaseZ });

export async function listLeases(params: { status?: string } = {}): Promise<Lease[]> {
  const res = await apiClient.get('/leases', { params });
  return ListLeasesZ.parse(res.data).data;
}

export async function getLease(id: number): Promise<Lease> {
  const res = await apiClient.get(`/leases/${id}`);
  return ResourceLeaseZ.parse(res.data).data;
}

const SignInitiateZ = z.object({ data: z.object({ status: z.string() }) });

/** Tenant: запросить OTP на телефон для подписания договора. */
export async function signInitiate(id: number): Promise<{ status: string }> {
  const res = await apiClient.post(`/leases/${id}/sign/initiate`);
  return SignInitiateZ.parse(res.data).data;
}

/** Tenant: ввод кода. При успехе договор подписан и активирован. */
export async function signVerify(id: number, code: string): Promise<Lease> {
  const res = await apiClient.post(`/leases/${id}/sign/verify`, { code });
  return ResourceLeaseZ.parse(res.data).data;
}

export const leasesApi = { list: listLeases, get: getLease, signInitiate, signVerify };
